import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Divider,
  Grid,
  Paper,
  Switch,
  FormControlLabel,
  Collapse,
  Button,
  Chip,
  Tooltip,
  IconButton
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  ColorLens as ColorLensIcon,
  CropRotate as CropRotateIcon
} from "@mui/icons-material";
import { convertDateToWords } from "../utils/dateToWords";

const toCamelCase = (str) => {
  if (!str) return "";
  return str
    ?.toLowerCase()
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .split(' ')
    .map((word) => word ? word.charAt(0).toUpperCase() + word.slice(1) : word)
    .join(' ');
};

// Student Info Fields Configuration
const STUDENT_INFO_FIELDS = {
  photo: { label: "Photo", defaultVisible: true },
  rollNo: { label: "Roll No", defaultVisible: true },
  name: { label: "Student's Name", defaultVisible: true },
  father: { label: "Father's Name", defaultVisible: true },
  mother: { label: "Mother's Name", defaultVisible: true },
  apaarId: { label: "Apaar ID", defaultVisible: true },
  aadhaarNumber: { label: "Aadhaar Number", defaultVisible: false },
  studied: { label: "Studied (Class)", defaultVisible: true },
  admissionNo: { label: "Admission No.", defaultVisible: true },
  standard: { label: "Standard", defaultVisible: false },
  casteCategory: { label: "Category/Caste", defaultVisible: false },
  dob: { label: "Date of Birth", defaultVisible: true },
  dobInWords: { label: "DOB (In Words)", defaultVisible: false },
  heightWeight: { label: "Height/Weight", defaultVisible: false },
  contact: { label: "Contact Number", defaultVisible: false },
  address: { label: "Address", defaultVisible: false },
};

export default function TwoPageMarksheetReceipt({ receiptData, school, setTemID }) {
  const student = receiptData.data || [];
  const receiptId = `marksheet`;

  // --- Customization controls ---
  const [themeColor, setThemeColor] = useState("#1976d2");
  const [showSettings, setShowSettings] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [fieldVisibility, setFieldVisibility] = useState(() => {
    const saved = localStorage.getItem('twoPageMarksheetFieldVisibility');
    if (saved) {
      return JSON.parse(saved);
    }
    const defaults = {};
    Object.entries(STUDENT_INFO_FIELDS).forEach(([key, field]) => {
      defaults[key] = field.defaultVisible;
    });
    return defaults;
  });

  useEffect(() => {
    if (setTemID) {
      setTemID(receiptId);
    }
  }, [receiptData, setTemID, receiptId]);

  // Save visibility settings
  useEffect(() => {
    localStorage.setItem('twoPageMarksheetFieldVisibility', JSON.stringify(fieldVisibility));
  }, [fieldVisibility]);

  // Add landscape styles to document
  useEffect(() => {
    if (isLandscape) {
      const style = document.createElement('style');
      style.id = 'landscape-style-two-page';
      style.innerHTML = `
        @media print {
          @page {
            size: landscape;
            margin: 8mm;
          }
          .pagebreak {
            page-break-after: always;
          }
        }
        .landscape-container {
          max-width: 1100px !important;
        }
        .portrait-container {
          max-width: 800px !important;
        }
      `;
      document.head.appendChild(style);
    } else {
      const existingStyle = document.getElementById('landscape-style-two-page');
      if (existingStyle) {
        existingStyle.remove();
      }
    }
    return () => {
      const existingStyle = document.getElementById('landscape-style-two-page');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [isLandscape]);

  const activities = [
    { name: "Formative Assessment-I" },
    { name: "Formative Assessment-II" },
    { name: "Final-Exam" },
  ];

  const totalGrade = [
    { name: "Semester" },
    { name: "Education" },
    { name: "Co-Curricular" },
    { name: "Total" },
    { name: "Special Remark" },
    { name: "C. Teacher Signature" },
  ];

  const totalActivity = [
    { name: "Qualities" },
    { name: "Creativity" },
    { name: "Regularity" },
    { name: "Punctuality" },
    { name: "Cleanness" },
    { name: "Cultural" },
    { name: "Discipline" },
    { name: "Confidence" },
    { name: "School Program" },
    { name: "School Mates " },
  ];

  // extract unique subjects
  const subjects = useMemo(() => {
    const allMarks = student.flatMap((ms) => ms.marks || []);
    const unique = [...new Map(allMarks.map((m) => [m.subjectName?.trim(), m])).values()];
    return unique.sort((a, b) =>
      a.subjectName?.toLowerCase().localeCompare(b.subjectName?.toLowerCase())
    );
  }, [student]);

  // quick lookup
  const findMark = (examType, subject) => {
    const term = student.find((m) => m.examType?.trim().toLowerCase()
      .includes(examType?.trim().toLowerCase()));
    return term?.marks?.find(
      (s) => s.subjectName?.trim().toLowerCase() === subject?.trim().toLowerCase()
    );
  };

  const getTotals = (examType, rem) => {
    const term = student.find((m) => m.examType?.trim().toLowerCase()
      .includes(examType?.trim().toLowerCase()));
    if (!term || !term.marks) return { max: 0, obt: 0, grade: "-" };
    const max = term.marks.filter(s => s.remark !== rem).reduce((sum, s) => sum + (s.maxMark || 0), 0);
    const obt = term.marks.filter(s => s.remark !== rem).reduce((sum, s) => sum + (s.marksObtained || 0), 0);
    return { max, obt, grade: term.grade || "-" };
  };

  const getTotalsNoon = (examType, rem) => {
    const term = student.find((m) => m.examType?.trim().toLowerCase()
      .includes(examType?.trim().toLowerCase()));
    if (!term || !term.marks) return { max: 0, obt: 0, grade: "-" };
    const max = term.marks.filter(s => s.remark === rem).reduce((sum, s) => sum + (s.maxMark || 0), 0);
    const obt = term.marks.filter(s => s.remark === rem).reduce((sum, s) => sum + (s.marksObtained || 0), 0);
    return { max, obt, grade: term.grade || "-" };
  };

  const [terms, setTerms] = useState([
    { n: "First" },
    { n: "Second" },
    { n: "Final" }
  ]);

  const t1 = getTotals("First", "non");
  const t2 = getTotals("Second", "non");
  const t3 = getTotals("Final", "non");

  const t4 = getTotalsNoon("First", "non");
  const t5 = getTotalsNoon("Second", "non");
  const t6 = getTotalsNoon("Final", "non");

  const borderColor = themeColor;
  const headerBg = alpha(themeColor, 0.14);

  // Dynamic cell styles based on landscape mode
  const cellPadding = isLandscape ? "4px" : "6px";
  const cell = { border: `1px solid ${borderColor}`, padding: cellPadding };
  const cell1 = { border: `1px solid ${borderColor}`, padding: isLandscape ? "2px 3px" : "0px 2px" };

  // Toggle field visibility
  const toggleFieldVisibility = (fieldKey) => {
    setFieldVisibility(prev => ({
      ...prev,
      [fieldKey]: !prev[fieldKey]
    }));
  };

  // Toggle all fields
  const toggleAllFields = (visible) => {
    const newState = {};
    Object.keys(STUDENT_INFO_FIELDS).forEach(key => {
      newState[key] = visible;
    });
    setFieldVisibility(newState);
  };

  // Get visibility count
  const visibleCount = Object.values(fieldVisibility).filter(v => v).length;
  const totalFields = Object.keys(STUDENT_INFO_FIELDS).length;

  // Toggle landscape mode
  const toggleLandscape = () => {
    setIsLandscape(!isLandscape);
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Handle download
  const handleDownload = () => {
    alert("Download functionality coming soon!");
  };

  const MarksRow = (term) =>
    term?.remark !== "non" && (
      <>
        <TableCell sx={cell} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
          {term?.maxMark ?? 100}
        </TableCell>
        <TableCell sx={cell} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
          {((term?.maxMark || 100) * 34) / 100}
        </TableCell>
        <TableCell sx={cell} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
          {term?.marksObtained ?? "-"}
        </TableCell>
        <TableCell sx={cell} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
          {term?.grade ?? "-"}
        </TableCell>
      </>
    );

  const MarksRowNon = (term) =>
    term?.remark === "non" && (
      <>
        <TableCell sx={cell} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
          {term?.maxMark ?? 100}
        </TableCell>
        <TableCell sx={cell} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
          {((term?.maxMark || 100) * 34) / 100}
        </TableCell>
        <TableCell sx={cell} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
          {term?.marksObtained ?? "-"}
        </TableCell>
        <TableCell sx={cell} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
          {term?.grade ?? "-"}
        </TableCell>
      </>
    );

  const renderStudentField = (fieldKey) => {
    if (!fieldVisibility[fieldKey]) return null;

    const studentData = student.find((m) =>
      m.examType?.toLowerCase().includes('first'));

    const field = STUDENT_INFO_FIELDS[fieldKey];
    let value = '';

    switch (fieldKey) {
      case 'apaarId':
        value = studentData?.apaarId || '';
        break;
      case 'name':
        value = toCamelCase(`${studentData?.name || ''} ${studentData?.last || ''}`.trim());
        break;
      case 'father':
        value = toCamelCase(`${studentData?.father || ''} ${studentData?.last || ''}`.trim());
        break;
      case 'mother':
        value = toCamelCase(`${studentData?.mother || ''} ${studentData?.last || ''}`.trim());
        break;
      case 'studied':
        value = `${studentData?.classRoom?.standard || ''} | ${studentData?.classRoom?.section || ''}`;
        break;
      case 'admissionNo':
        value = studentData?.id || '';
        break;
      case 'casteCategory':
        value = `${studentData?.category} | caste: ${studentData?.caste || ''}`;
        break;
      case 'dob':
        value = studentData?.birthday ? new Date(studentData.birthday).toLocaleDateString("en-GB") : '';
        break;
      case 'dobInWords':
        value = studentData?.birthday ? convertDateToWords(studentData.birthday) : '';
        break;
      case 'heightWeight':
        return (
          <Box key={fieldKey} display={'flex'} justifyContent={'space-between'} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="body2" component="span" fontWeight={700}>{field.label}</Typography>
            <TextField
              variant="standard"
              placeholder="Height/Weight"
              size="small"
              slotProps={{
                input: {
                  disableUnderline: true,
                },
              }}
            />
          </Box>
        );
      case 'photo':
        return studentData?.photo ? (
          <Box key={fieldKey} display="flex" justifyContent="center" my={0.5}>
            <img
              src={studentData.photo}
              alt="Student"
              style={{ width: isLandscape ? 70 : 100, height: isLandscape ? 70 : 100, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${borderColor}` }}
              onError={(e) => e.target.style.display = 'none'}
            />
          </Box>
        ) : null;
      case 'aadhaarNumber':
        value = studentData?.aadhaar || '';
        break;
      case 'contact':
        value = studentData?.contact || studentData?.mobile || '';
        break;
      case 'standard':
        value = `${studentData?.classRoom?.standard || ''} | ${studentData?.classRoom?.section || ''}`;
        break;
      case 'address':
        value = toCamelCase([studentData?.address, studentData?.address?.city, studentData?.address?.pincode, studentData?.address?.state].filter(Boolean).join(", ")) ||
          '';
        break;
      case 'rollNo':
        value = studentData?.rollNumber || studentData?.id || '';
        break;
      default:
        return null;
    }

    if (!value || value === 'N/A') return null;

    return (
      <Box
        key={fieldKey}
        sx={{
          display: "grid",
          gridTemplateColumns: isLandscape ? "140px auto" : "180px auto",
          mb: 0.3,
          gap: 0.5,
          alignItems: "center"
        }}
      >
        <Typography sx={{ fontWeight: "bold", fontSize: isLandscape ? "0.75rem" : "0.9rem" }} component="span">
          {field.label} :
        </Typography>
        <Typography sx={{ fontSize: isLandscape ? "0.75rem" : "0.9rem" }} component="span">
          {value}
        </Typography>
      </Box>
    );
  };

  const visibleFields = Object.keys(STUDENT_INFO_FIELDS)
    .filter(key => fieldVisibility[key]);

  const data = student.find((m) =>
    m.examType?.toLowerCase().includes('first'));

  // Dynamic max width based on landscape mode
  const containerMaxWidth = isLandscape ? "1100px" : "900px";

  return (
    <Box id={receiptId}>
      {/* Controls Panel */}
      <Paper
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "center",
          maxWidth: containerMaxWidth,
          margin: "auto",
          mb: 2,
          p: 1.5,
          "@media print": { display: "none" },
        }}
        elevation={2}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight={600} fontSize="0.8rem" component="span">
            <ColorLensIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
            Color:
          </Typography>
          <input
            type="color"
            value={themeColor}
            onChange={(e) => setThemeColor(e.target.value)}
            style={{
              width: 32,
              height: 28,
              padding: 0,
              border: "1px solid #ccc",
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        </Box>

        <Button
          size="small"
          variant={isLandscape ? "contained" : "outlined"}
          startIcon={<CropRotateIcon />}
          onClick={toggleLandscape}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          {isLandscape ? "Landscape" : "Portrait"}
        </Button>

        <Button
          size="small"
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => setShowSettings(!showSettings)}
          sx={{ fontSize: "0.75rem", textTransform: "none" }}
        >
          Customize
        </Button>

        <Tooltip title={`${visibleCount}/${totalFields} fields visible`}>
          <Chip
            size="small"
            label={`${visibleCount}/${totalFields}`}
            color="primary"
            variant="outlined"
            sx={{ height: 28 }}
          />
        </Tooltip>

        <IconButton size="small" onClick={handlePrint} title="Print">
          <PrintIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={handleDownload} title="Download">
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Paper>

      {/* Settings Panel */}
      <Collapse in={showSettings}>
        <Paper
          sx={{
            maxWidth: containerMaxWidth,
            margin: "auto",
            mb: 2,
            p: 2,
            bgcolor: '#f8f9fa',
            "@media print": { display: "none" },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" fontWeight="600" fontSize="0.85rem" component="span">
              Toggle Student Info Fields
            </Typography>
            <Box display="flex" gap={1}>
              <Button size="small" onClick={() => toggleAllFields(true)} sx={{ fontSize: "0.75rem" }}>
                Show All
              </Button>
              <Button size="small" onClick={() => toggleAllFields(false)} sx={{ fontSize: "0.75rem" }}>
                Hide All
              </Button>
            </Box>
          </Box>
          <Grid container spacing={0.5}>
            {Object.entries(STUDENT_INFO_FIELDS).map(([key, field]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={fieldVisibility[key] || false}
                      onChange={() => toggleFieldVisibility(key)}
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={0.5} component="span">
                      <Typography variant="caption" fontSize="0.7rem" component="span">{field.label}</Typography>
                      {fieldVisibility[key] ? (
                        <VisibilityIcon fontSize="small" color="primary" sx={{ fontSize: 14 }} />
                      ) : (
                        <VisibilityOffIcon fontSize="small" color="disabled" sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Collapse>

      {/* Page 1 - Marks Table */}
      <Box sx={{
        p: isLandscape ? 1.5 : 1,
        background: "#fff",
        fontFamily: "Arial",
        maxWidth: containerMaxWidth,
        margin: "auto",
        border: `2px solid ${borderColor}`,
        borderRadius: "12px",
        backgroundColor: "#fff",
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: "column", alignItems: "center", mb: 0, mt: 0 }}>
          <Typography variant={isLandscape ? "subtitle2" : "subtitle1"} align="center" fontWeight="bold" sx={{ color: borderColor }} component="span">
            PROGRESS REPORT SESSION - {student[0]?.academicYear}
          </Typography>
        </Box>

        <Divider sx={{ my: 0.5, borderColor }} />

        {/* Student Info on first page */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5, flexWrap: "wrap", gap: 0.5 }}>
          <Box component="span" sx={{ fontSize: isLandscape ? "0.75rem" : "0.875rem" }}>
            <strong>Student's Name: </strong> {toCamelCase(`${student[0]?.studentName || student[0]?.name || ''}`)}
          </Box>
          <Box component="span" sx={{ fontSize: isLandscape ? "0.75rem" : "0.875rem" }}>
            <strong>Standard: </strong>{student[0]?.classRoom?.standard} | {student[0]?.classRoom?.section}
          </Box>
          <Box component="span" sx={{ fontSize: isLandscape ? "0.75rem" : "0.875rem" }}>
            <strong>Roll No.: </strong> {student[0]?.rollNumber}
          </Box>
        </Box>

        {/* Marks Table */}
        <Box sx={{ p: 0, mb: 0.5, overflowX: 'auto' }}>
          <Table size={isLandscape ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} rowSpan={2} align="center">
                  <strong style={{ fontSize: isLandscape ? "0.7rem" : "0.85rem" }}>Subject</strong>
                </TableCell>
                <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} colSpan={4} align="center">
                  <TextField
                    sx={{
                      mt: -0.5,
                      mb: -1,
                      "& .MuiInputBase-input": {
                        fontWeight: 700,
                        color: borderColor,
                        fontSize: isLandscape ? "0.7rem" : "0.85rem"
                      },
                    }}
                    variant="standard"
                    placeholder="Term-I"
                    slotProps={{
                      input: {
                        disableUnderline: true,
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} colSpan={4} align="center">
                  <TextField
                    sx={{
                      mt: -0.5,
                      mb: -1,
                      "& .MuiInputBase-input": {
                        fontWeight: 700,
                        color: borderColor,
                        fontSize: isLandscape ? "0.7rem" : "0.85rem"
                      },
                    }}
                    variant="standard"
                    placeholder="Term-II"
                    slotProps={{
                      input: {
                        disableUnderline: true,
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} colSpan={4} align="center">
                  <TextField
                    sx={{
                      mt: -0.5,
                      mb: -1,
                      "& .MuiInputBase-input": {
                        fontWeight: 700,
                        color: borderColor,
                        fontSize: isLandscape ? "0.7rem" : "0.85rem"
                      },
                    }}
                    variant="standard"
                    placeholder="Final-Exam"
                    slotProps={{
                      input: {
                        disableUnderline: true,
                      },
                    }}
                  />
                </TableCell>
              </TableRow>

              <TableRow>
                {[...Array(3)].flatMap((_, index) => [
                  <TableCell key={`max-${index}`} sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.65rem" : "0.8rem" }}>Max</TableCell>,
                  <TableCell key={`min-${index}`} sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.65rem" : "0.8rem" }}>Min</TableCell>,
                  <TableCell key={`obt-${index}`} sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.65rem" : "0.8rem" }}>Obt</TableCell>,
                  <TableCell key={`grade-${index}`} sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.65rem" : "0.8rem" }}>Grade</TableCell>,
                ])}
              </TableRow>
            </TableHead>

            <TableBody>
              {subjects.map((sub, i) => {
                const term1 = findMark("First", sub.subjectName);
                const term2 = findMark("second", sub.subjectName);
                const finalExam = findMark("Final", sub.subjectName);
                return (
                  <TableRow key={`subject-${i}`}>
                    {sub.remark !== 'non' && (
                      <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
                        {toCamelCase(sub.subjectName)}
                      </TableCell>
                    )}
                    {MarksRow(term1)}
                    {MarksRow(term2)}
                    {MarksRow(finalExam)}
                  </TableRow>
                );
              })}

              {/* Totals Row */}
              <TableRow sx={{ background: alpha(borderColor, 0.06) }} key="totals-row">
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }}>
                  <strong style={{ fontSize: isLandscape ? "0.7rem" : "0.85rem" }}>Total Mark</strong>
                </TableCell>

                {/* Term-I */}
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t1.max}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} />
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t1.obt}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t1.grade}</TableCell>

                {/* Term-II */}
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t2.max}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} />
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t2.obt}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t2.grade}</TableCell>

                {/* Final Exam */}
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t3.max}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} />
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t3.obt}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t3.grade}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <Typography variant={isLandscape ? "subtitle2" : "subtitle1"} align="center" fontWeight="bold" sx={{ color: borderColor }} >
          {"NON ACADEMIC PERFORMANCE"}
        </Typography>

        <Box sx={{ p: 0, mb: 0.5, overflowX: 'auto' }}>
          <Table size={isLandscape ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} rowSpan={2} align="center">
                  <strong style={{ fontSize: isLandscape ? "0.7rem" : "0.85rem" }}>Subject</strong>
                </TableCell>

                <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} colSpan={4} align="center">
                  <TextField
                    sx={{
                      mt: -0.5,
                      mb: -1,
                      "& .MuiInputBase-input": {
                        fontWeight: 700,
                        color: borderColor,
                        fontSize: isLandscape ? "0.7rem" : "0.85rem"
                      },
                    }}
                    variant="standard"
                    placeholder="Term-I"
                    slotProps={{
                      input: {
                        disableUnderline: true,
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} colSpan={4} align="center">
                  <TextField
                    sx={{
                      mt: -0.5,
                      mb: -1,
                      "& .MuiInputBase-input": {
                        fontWeight: 700,
                        color: borderColor,
                        fontSize: isLandscape ? "0.7rem" : "0.85rem"
                      },
                    }}
                    variant="standard"
                    placeholder="Term-II"
                    slotProps={{
                      input: {
                        disableUnderline: true,
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} colSpan={4} align="center">
                  <TextField
                    sx={{
                      mt: -0.5,
                      mb: -1,
                      "& .MuiInputBase-input": {
                        fontWeight: 700,
                        color: borderColor,
                        fontSize: isLandscape ? "0.7rem" : "0.85rem"
                      },
                    }}
                    variant="standard"
                    placeholder="Final Exam"
                    slotProps={{
                      input: {
                        disableUnderline: true,
                      },
                    }}
                  />
                </TableCell>
              </TableRow>

              <TableRow>
                {[...Array(3)].flatMap((_, index) => [
                  <TableCell key={`non-max-${index}`} sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.65rem" : "0.8rem" }}>Max</TableCell>,
                  <TableCell key={`non-min-${index}`} sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.65rem" : "0.8rem" }}>Min</TableCell>,
                  <TableCell key={`non-obt-${index}`} sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.65rem" : "0.8rem" }}>Obt</TableCell>,
                  <TableCell key={`non-grade-${index}`} sx={{ ...cell, background: headerBg, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.65rem" : "0.8rem" }}>Grade</TableCell>,
                ])}
              </TableRow>
            </TableHead>

            <TableBody>
              {subjects.map((sub, i) => {
                const term1 = findMark("First", sub.subjectName);
                const term2 = findMark("Second", sub.subjectName);
                const finalExam = findMark("Final", sub.subjectName);
                return (
                  <TableRow key={`non-subject-${i}`}>
                    {sub.remark?.trim().toLowerCase() === 'non' && (
                      <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>
                        {toCamelCase(sub.subjectName)}
                      </TableCell>
                    )}
                    {MarksRowNon(term1)}
                    {MarksRowNon(term2)}
                    {MarksRowNon(finalExam)}
                  </TableRow>
                );
              })}

              {/* Totals Row */}
              <TableRow sx={{ background: alpha(borderColor, 0.06) }} key="non-totals-row">
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }}>
                  <strong style={{ fontSize: isLandscape ? "0.7rem" : "0.85rem" }}>Total Mark</strong>
                </TableCell>

                {/* Term-I */}
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t4.max}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} />
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t4.obt}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t4.grade}</TableCell>

                {/* Term-II */}
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t5.max}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} />
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t5.obt}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t5.grade}</TableCell>

                {/* Final Exam */}
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t6.max}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} />
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t6.obt}</TableCell>
                <TableCell sx={{ ...cell, padding: isLandscape ? "4px 6px" : "6px" }} align="center" style={{ fontSize: isLandscape ? "0.7rem" : "0.8rem" }}>{t6.grade}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        {/* Result Section */}
        <Box sx={{ display: "flex", alignItems: 'center', justifyContent: "space-around", mb: 1, flexWrap: "wrap", gap: 0.5 }}>
          <Box component="span" sx={{ fontSize: isLandscape ? "0.7rem" : "0.875rem" }}>
            <strong>Attendance Sem I & II - </strong>
            <TextField
              sx={{
                mt: 0,
                width: '25%',
                "& .MuiInputBase-input": {
                  fontSize: isLandscape ? "0.7rem" : "0.875rem",
                },
              }}
              variant="standard"
              placeholder="................."
              slotProps={{
                input: {
                  disableUnderline: true,
                },
              }}
            />
          </Box>
          <Box component="span" sx={{ fontSize: isLandscape ? "0.7rem" : "0.875rem" }}>
            <strong>Promoted to Class </strong>
            <TextField
              sx={{
                mt: 0,
                width: '25%',
                "& .MuiInputBase-input": {
                  fontSize: isLandscape ? "0.7rem" : "0.875rem",
                },
              }}
              variant="standard"
              placeholder={student[0]?.classRoom?.standard}
              slotProps={{
                input: {
                  disableUnderline: true,
                },
              }}
            />
          </Box>
          <Box component="span" sx={{ fontSize: isLandscape ? "0.7rem" : "0.875rem" }}>
            <strong>Division : </strong>
            <TextField
              sx={{
                mt: 0,
                width: '25%',
                "& .MuiInputBase-input": {
                  fontSize: isLandscape ? "0.7rem" : "0.875rem",
                },
              }}
              variant="standard"
              placeholder={student[2]?.result}
              slotProps={{
                input: {
                  disableUnderline: true,
                },
              }}
            />
          </Box>
          <Box component="span" sx={{ fontSize: isLandscape ? "0.7rem" : "0.875rem" }}>
            <strong>Percentage:</strong> {student[2]?.percentage || 'N/A'}%
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ display: "flex", alignItems: 'center', justifyContent: "space-around", mb: 0.5 }}>
          <Box component="span" sx={{ fontSize: isLandscape ? "0.7rem" : "0.875rem" }}>Class Teacher Sign </Box>
          <Box component="span" sx={{ fontSize: isLandscape ? "0.7rem" : "0.875rem" }}>Head Master: </Box>
        </Box>
      </Box>

      <div className="pagebreak"></div>

      {/* Page 2 - Student Profile & Activities */}
      <Box sx={{
        display: 'flex',
        flexDirection: isLandscape ? 'row' : 'row',
        justifyContent: 'space-around',
        alignItems: 'stretch',
        maxWidth: containerMaxWidth,
        margin: "auto",
        pt: 0,
        border: `2px solid ${borderColor}`,
        borderRadius: "12px",
        backgroundColor: "#fff",
        gap: 0.5,
        flexWrap: "wrap"
      }}>
        {/* Left Column - Student Profile */}
        <Box sx={{ flex: isLandscape ? 1 : 1, minWidth: isLandscape ? "280px" : "280px", p: isLandscape ? 1 : 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 0.5 }}>
            <img
              src={school?.logoUrl}
              alt="School Logo"
              style={{ width: isLandscape ? 60 : 80, height: isLandscape ? 50 : 70, marginTop: 5 }}
              onError={(e) => e.target.style.display = 'none'}
            />

            <Typography variant={isLandscape ? "h6" : "h5"} align="center" fontWeight="bold" sx={{ color: borderColor }} component="span">
              {toCamelCase(school?.schoolName)}
            </Typography>
            <Typography variant="subtitle2" align="center" style={{ fontSize: isLandscape ? "0.6rem" : "0.8rem" }} component="span">
              {toCamelCase([school?.addressDTO?.addressLine1, school?.addressDTO?.city, school?.addressDTO?.pincode, school?.addressDTO?.state].filter(Boolean).join(", "))}
            </Typography>
            <Typography variant="subtitle2" align="center" fontWeight="bold" style={{ fontSize: isLandscape ? "0.7rem" : "0.85rem" }} component="span">
              PROGRESS REPORT SESSION - {student[0]?.academicYear}
            </Typography>

            {/* Photo */}
            {fieldVisibility.photo && data?.photo && (
              <img
                src={data.photo}
                alt="Student"
                style={{ width: isLandscape ? 70 : 100, height: isLandscape ? 70 : 100, marginTop: 5, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${borderColor}` }}
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
          </Box>

          <Typography variant="subtitle1" align="center" fontWeight="bold" sx={{ bgcolor: headerBg, py: 0.3, borderRadius: 1, fontSize: isLandscape ? "0.75rem" : "0.9rem" }} component="span">
            {"STUDENT PROFILE"}
          </Typography>
          <Divider sx={{ mb: 1, borderColor }} />

          {/* Student Info - Single Column */}
          <Box sx={{ display: "flex", flexDirection: "column", px: 1 }}>
            {visibleFields
              .filter(key => key !== 'photo')
              .map(key => renderStudentField(key))}
          </Box>
        </Box>

        <Divider
          //orientation={isLandscape ? "vertical" : "horizontal"} 
          orientation={"vertical"}
          flexItem sx={{ borderColor }} />

        {/* Right Column - Activities */}
        <Box sx={{ flex: isLandscape ? 1.2 : 1.2, minWidth: isLandscape ? "360px" : "360px", p: isLandscape ? 1 : 1 }}>
          {/* Activity Table */}
          <Box sx={{ mb: 1, overflowX: 'auto' }}>
            <Table size={isLandscape ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...cell1, background: headerBg, padding: isLandscape ? "2px 4px" : "4px" }} colSpan={activities.length + 1} align="center">
                    <strong style={{ fontSize: isLandscape ? "0.7rem" : "0.85rem" }}>Co-Curricular Activity</strong>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={{ ...cell1, background: headerBg, padding: isLandscape ? "2px 4px" : "4px" }} align="center" style={{ fontSize: isLandscape ? "0.6rem" : "0.75rem" }}>{'#'}</TableCell>
                  {activities.map((a, i) => (
                    <TableCell sx={{ ...cell1, background: headerBg, padding: isLandscape ? "2px 4px" : "4px" }} key={`activity-${i}`} align="center" style={{ fontSize: isLandscape ? "0.6rem" : "0.75rem" }}>
                      {a.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {totalActivity.map((sub, i) => {
                  return (
                    <TableRow key={`total-activity-${i}`}>
                      <TableCell sx={{ ...cell1, padding: isLandscape ? "1px 4px" : "2px 4px" }} style={{ fontSize: isLandscape ? "0.6rem" : "0.75rem" }}>
                        {toCamelCase(sub.name)}
                      </TableCell>
                      {activities.map((_, j) => (
                        <TableCell sx={{ ...cell1, padding: isLandscape ? "1px 4px" : "2px 4px" }} align="center" key={`act-${i}-${j}`}>
                          <TextField
                            variant="standard"
                            placeholder="Very Good"
                            slotProps={{
                              input: {
                                disableUnderline: true,
                              },
                            }}
                            sx={{
                              "& input": {
                                fontFamily: "inherit",
                                fontSize: isLandscape ? "0.6rem" : "0.75rem",
                                padding: "2px 4px",
                              },
                            }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {/* Total Grade Table */}
          <Box sx={{ mb: 1, overflowX: 'auto' }}>
            <Table size={isLandscape ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...cell1, background: headerBg, padding: isLandscape ? "2px 4px" : "4px" }} colSpan={totalGrade.length + 1} align="center">
                    <strong style={{ fontSize: isLandscape ? "0.7rem" : "0.85rem" }}>Total Grade</strong>
                  </TableCell>
                </TableRow>

                <TableRow>
                  {totalGrade.map((a, j) => (
                    <TableCell sx={{ ...cell1, background: headerBg, padding: isLandscape ? "2px 4px" : "4px" }} key={`grade-label-${j}`} align="center" style={{ fontSize: isLandscape ? "0.6rem" : "0.75rem" }}>
                      {a.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {subjects.map((sub, i) => {
                  return (
                    <TableRow key={`grade-row-${i}`}>
                      {i < 3 && totalGrade.map((_, j) => (
                        <TableCell key={`grade-cell-${i}-${j}`} sx={{ ...cell1, padding: isLandscape ? "1px 4px" : "2px 4px" }}>
                          {j === 0 ? (
                            terms[i]?.n || "-"
                          ) : (
                            <TextField
                              variant="standard"
                              placeholder=".........."
                              slotProps={{ input: { disableUnderline: true } }}
                              sx={{
                                "& input": {
                                  fontSize: isLandscape ? "0.6rem" : "0.75rem",
                                  padding: "2px 4px",
                                },
                              }}
                            />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          <TextField
            sx={{
              mt: 0,
              width: '100%',
              "& .MuiInputBase-input": {
                fontSize: isLandscape ? "0.7rem" : "0.875rem",
              },
            }}
            variant="standard"
            placeholder="Very Good"
            slotProps={{
              input: { disableUnderline: true },
            }}
          />

          <Box sx={{ display: "flex", alignItems: 'center', justifyContent: "space-around", my: 1 }}>
            <Box component="span" sx={{ fontSize: isLandscape ? "0.65rem" : "0.8rem" }}>Class Teacher sign </Box>
            <Box component="span" sx={{ fontSize: isLandscape ? "0.65rem" : "0.8rem" }}>Head Master </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}